/**
 * React adapter for @chatsdet/workflow-diff.
 *
 * Re-exports the pure diff engine (types + `diffSnapshots` + `fromApiSnapshot`)
 * plus a `fromClientState` helper that bridges the client-side React Flow
 * node/edge shape (`.data` / `.type` / `.source` / `.target`) into the
 * canonical snapshot shape the diff engine operates on.
 */

import type {
  WorkflowNode,
  WorkflowEdge,
} from '../../../../types/workflow';
import { topoOrderNodes, type DiffSnapshot } from '@chatsdet/workflow-diff';

export {
  diffSnapshots,
  fromApiSnapshot,
  type DiffNode,
  type DiffEdge,
  type DiffSnapshot,
  type FieldChange,
  type ChangedNodeDiff,
  type SnapshotDiff,
} from '@chatsdet/workflow-diff';

/**
 * Convert the client-side workflow state (nodes with `.data` / `.type`,
 * edges with `.source` / `.target`) into the canonical diff shape.
 *
 * `orderIndex` is assigned from topological (edge) order, not the raw React
 * Flow array position — a node added mid-flow is appended to the array but
 * belongs between its neighbours. This keeps a created proposal's stored
 * `orderIndex` consistent with the graph.
 */
export function fromClientState(
  nodes: WorkflowNode[],
  edges: WorkflowEdge[],
): DiffSnapshot {
  const diffEdges = edges.map((e) => ({
    id: e.id,
    sourceNodeId: e.source,
    targetNodeId: e.target,
    // Client hydration stores the type on `.type`; keep `.edgeType` as a fallback.
    edgeType: e.type ?? e.edgeType ?? null,
  }));
  const diffNodes = nodes.map((n) => ({
    id: n.id,
    nodeType: n.type ?? 'unknown',
    configJson: (n.data ?? {}) as Record<string, unknown>,
  }));
  const ordered = topoOrderNodes(diffNodes, diffEdges);
  return {
    nodes: ordered.map((n, i) => ({ ...n, orderIndex: i })),
    edges: diffEdges,
  };
}
